import {
  Alert,
  DatePicker,
  Dialog,
  FormStack,
  InputAdornment,
  MenuItem,
  SelectField,
  Stack,
  TextField,
} from '@all-manager/design-system-v2';
import { Controller } from 'react-hook-form';
import { ExpenseCategoryDatabase, ExpenseDatabase } from '../../../../types';
import { useExpenseFormDialog } from './useExpenseFormDialog';

type Props = {
  onClose: () => void;
  categories: ExpenseCategoryDatabase[];
  expense?: ExpenseDatabase;
  defaultCategoryId?: string;
};

const AMOUNT_INPUT_PROPS = {
  htmlInput: { inputMode: 'decimal' },
  input: { endAdornment: <InputAdornment position="end">€</InputAdornment> },
} as const;

const MONTH_VIEWS = ['year', 'month'] as const;

export default function ExpenseFormDialog(props: Props) {
  const hook = useExpenseFormDialog(props);

  return (
    <Dialog
      open
      onClose={hook.handleClose}
      onSubmit={hook.onSubmit}
      maxWidth="sm"
      fullWidth
      title={hook.isEdition ? 'Modifier la dépense' : 'Nouvelle dépense'}
      closeLabel="Fermer"
      actions={{
        primaryAction: {
          label: hook.isEdition ? 'Enregistrer la dépense' : 'Ajouter la dépense',
          type: 'submit',
          loading: hook.isPending,
        },
        secondaryAction: {
          label: 'Annuler',
          onClick: hook.handleClose,
          disabled: hook.isPending,
        },
      }}
    >
      <Stack spacing={2}>
        {hook.serverError ? <Alert severity="error">{hook.serverError}</Alert> : null}

        <Controller
          control={hook.form.control}
          name="categoryId"
          render={({ field, fieldState }) => (
            <SelectField<string>
              name={field.name}
              inputRef={field.ref}
              onBlur={field.onBlur}
              onChange={(event) => field.onChange(event.target.value)}
              value={field.value}
              label="Catégorie"
              fullWidth
              required
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            >
              {props.categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </SelectField>
          )}
        />

        <FormStack direction="row">
          <Controller
            control={hook.form.control}
            name="amount"
            render={({ field, fieldState }) => (
              <TextField
                name={field.name}
                inputRef={field.ref}
                onBlur={field.onBlur}
                onChange={field.onChange}
                value={field.value}
                label="Montant"
                fullWidth
                required
                slotProps={AMOUNT_INPUT_PROPS}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />

          <Controller
            control={hook.form.control}
            name="month"
            render={({ field, fieldState }) => (
              <DatePicker
                label="Mois"
                value={field.value}
                onChange={field.onChange}
                inputRef={field.ref}
                views={MONTH_VIEWS}
                openTo="month"
                format="MMMM yyyy"
                required
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </FormStack>
      </Stack>
    </Dialog>
  );
}
